import React, { useEffect, useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ExternalLinkIcon, ChevronDownIcon } from 'lucide-react';
import { CTABanner } from './CTABanner';
import { CapabilityModal } from './CapabilityModal';
export interface Feature {
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
  detailedDescription?: string;
  image?: string;
}
export interface FAQ {
  question: string;
  answer: string;
}
interface SolutionPageLayoutProps {
  label: string;
  title: string;
  subtitle: string;
  features: Feature[];
  faqs?: FAQ[];
}
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45
    }
  }
};
export function SolutionPageLayout({
  label,
  title,
  subtitle,
  features,
  faqs
}: SolutionPageLayoutProps) {
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <>
      {/* Hero */}
      <section className="relative w-full pt-32 pb-20 px-6 overflow-hidden bg-navy" data-id="element-260">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-261">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full animate-glow-pulse" style={{
          background: 'radial-gradient(ellipse at center, rgba(108, 92, 231, 0.2) 0%, rgba(200, 80, 192, 0.08) 40%, transparent 70%)'
        }} data-id="element-262" />

        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-263">
          <motion.span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-264">

            {label}
          </motion.span>

          <motion.h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} data-id="element-265">

            {title}
          </motion.h1>

          <motion.p className="text-lg text-white/55 max-w-2xl mx-auto mb-10 leading-relaxed" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} data-id="element-266">

            {subtitle}
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.35
        }} data-id="element-267">

            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-268">

              Get a Demo
              <ArrowRightIcon className="w-4 h-4" data-id="element-269" />
            </a>
            <a href="https://app.thruline.co/non-auth/signup" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/25 text-white font-medium text-sm hover:border-white/50 transition-all" data-id="element-270">

              Sign up for free
            </a>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-navy-light to-transparent pointer-events-none" data-id="element-271" />
      </section>

      {/* Features Grid */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-272">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-273" />

        <div className="max-w-5xl mx-auto" data-id="element-274">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5
        }} data-id="element-275">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-276">
              Capabilities
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-277">
              What's included
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-278">

            {features.map(feature => {
            const Icon = feature.icon;
            return <motion.button key={feature.title} variants={itemVariants} onClick={() => setSelectedFeature(feature)} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-7 backdrop-blur-sm text-left cursor-pointer group hover:border-violet-electric/20 hover:bg-white/[0.05] transition-all duration-200" data-id="element-279">

                  <div className="flex items-start justify-between mb-4" data-id="element-280">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-electric/20 to-violet-electric/5 flex items-center justify-center" data-id="element-281">
                      <Icon className="w-5 h-5 text-violet-electric" data-id="element-282" />
                    </div>
                    <ExternalLinkIcon className="w-4 h-4 text-white/0 group-hover:text-white/30 transition-all duration-200 mt-1" data-id="element-283" />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2 group-hover:text-white transition-colors" data-id="element-284">
                    {feature.title}
                  </h3>
                  <p className="text-white/45 text-sm leading-relaxed" data-id="element-285">
                    {feature.description}
                  </p>
                </motion.button>;
          })}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && <section className="relative w-full bg-navy py-24 px-6" data-id="element-286">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" data-id="element-287" />

          <div className="max-w-3xl mx-auto" data-id="element-288">
            <motion.div className="text-center mb-14" initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5
        }} data-id="element-289">

              <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-290">
                FAQ
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-291">
                Common questions
              </h2>
            </motion.div>

            <motion.div className="space-y-3" initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-292">

              {faqs.map((faq, index) => <div key={index} className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-id="element-293">

                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-5 text-left cursor-pointer group" data-id="element-294">

                    <span className="text-white font-medium text-sm pr-4 group-hover:text-white/90 transition-colors" data-id="element-295">
                      {faq.question}
                    </span>
                    <ChevronDownIcon className={`w-4 h-4 text-white/40 flex-shrink-0 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-296" />

                  </button>
                  <AnimatePresence data-id="element-297">
                    {openFaq === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-298">

                        <div className="px-5 pb-5 pt-0" data-id="element-299">
                          <p className="text-white/50 text-sm leading-relaxed" data-id="element-300">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </motion.div>
          </div>
        </section>}

      {/* CTA */}
      <CTABanner data-id="element-301" />

      {/* Modal */}
      <CapabilityModal feature={selectedFeature} onClose={() => setSelectedFeature(null)} data-id="element-302" />

    </>;
}